/**
 * Calendar Connections sagas.
 *
 * Handles the full connect lifecycle for Apple (CalDAV credentials →
 * discover → multi-select → save) and Google (OAuth popup → discover
 * → multi-select → save) plus management ops on existing rows
 * (sync, disconnect, edit selection).
 */

import { call, put, takeLatest, select } from 'redux-saga/effects';
import { withSagaFlow, emitSagaPoint } from '@/features/qpl-client/saga-helpers';
import {
  listCalendarConnections,
  appleCalendarSetup,
  appleCalendarConnect,
  appleCalendarSync,
  appleCalendarDisconnect,
  googleCalendarAuthURL,
  googleCalendarComplete,
  googleCalendarDisconnect,
  googleCalendarSync,
  listGoogleCalendars,
  updateConnectionSelection,
  listSubcalendars,
  updateSubcalendar as updateSubcalendarAPI,
} from '@/infra/api/client';
import {
  fetchEvents,
  calendarEventsPatched,
  addEvent,
  replaceEvent,
  removeEvent,
  calendarEventsRemovedByConnection,
} from '@/features/calendar/ducks';
import type { CalendarEvent as DomainCalendarEvent } from '@/infra/types/domain';
import {
  setConnections,
  setConnectionsLoading,
  setConnectionsError,
  setModalStep,
  setModalProvider,
  setDiscoveredApple,
  setDiscoveredGoogle,
  setPendingApple,
  setManaging,
  setModalSubmitting,
  setModalError,
  setManageSubcalendars,
  patchManageSubcalendar,
  closeConnectModal,
  connectionSyncCompleted,
  FETCH_CONNECTIONS,
  APPLE_SETUP,
  APPLE_CONNECT,
  GOOGLE_BEGIN,
  GOOGLE_AFTER_CALLBACK,
  SAVE_SELECTION,
  SYNC_CONNECTION,
  DISCONNECT_CONNECTION,
  OPEN_MANAGE,
  UPDATE_SUBCAL,
  fetchCalendarConnections,
} from './ducks';
import type {
  CalendarConnection,
  AppleCalendarSetupResponse,
  AppleCalendarConnectionResponse,
  AppleCalendarSyncResponse,
  GoogleAuthInitResponse,
  GoogleCalendarListResponse,
  GoogleCalendarSyncResponse,
  Subcalendar,
} from '@/infra/contracts/calendar';

const POPUP_SUCCESS = 'calendar-connections/google-callback-success';
const POPUP_ERROR = 'calendar-connections/google-callback-error';

// ─── Fetch list ───────────────────────────────────────────

export function* fetchConnectionsSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'calendarConnections.fetch', ['calendar-connections'], action, function* () {
    yield put(setConnectionsLoading(true));
    try {
      const list = (yield call(listCalendarConnections)) as CalendarConnection[];
      yield* emitSagaPoint('api_listCalendarConnections_ok', { count: list.length });
      yield put(setConnections(list));
      yield put(setConnectionsError(null));
    } catch (error: any) {
      yield put(setConnectionsError(error.message || 'Failed to load calendar connections'));
      yield* emitSagaPoint('api_listCalendarConnections_err', { message: error?.message });
    } finally {
      yield put(setConnectionsLoading(false));
    }
  });
}

// ─── Apply sync outcome ───────────────────────────────────
//
// Shared tail used by appleConnectSaga, saveSelectionSaga, and
// syncConnectionSaga after a sync RPC succeeds. Centralizes the "what
// changed in Redux when sync finishes" decision so the three call sites
// stay aligned.
//
// Per feedback_saga_full_delta: the sync response IS the delta. The
// server returns a CalendarConnectionDelta containing created /
// updated / deleted events plus connection-level metadata
// (last_synced_at, status). The saga dispatches one Redux action per
// mutated entity so the local store mirrors the post-write state the
// moment the API returns — no fetchEvents() hammer.
//
// Backwards compat: a void / undefined response (e.g. an early error
// path that swallowed the body) still drives the connection-row
// patch with an empty diff so the stale-indicator clears.
export type SyncOutcomeResponse =
  | AppleCalendarSyncResponse
  | GoogleCalendarSyncResponse
  | void
  | undefined;

export function* applySyncOutcomeSaga(
  connectionId: string,
  response: SyncOutcomeResponse,
): Generator {
  const patch: { last_synced_at?: string; status?: string } = {};
  let delta: import('@/infra/contracts/calendar').CalendarConnectionDelta | undefined;
  if (response && typeof response === 'object') {
    const r = response as Partial<AppleCalendarSyncResponse> &
      Partial<GoogleCalendarSyncResponse>;
    delta = r.delta;
    // Prefer the delta's connection-level fields (canonical) and fall
    // back to the legacy top-level Apple-shaped fields when the delta
    // is absent.
    if (delta?.last_synced_at) patch.last_synced_at = delta.last_synced_at;
    else if (typeof r.last_sync === 'string' && r.last_sync) patch.last_synced_at = r.last_sync;
    if (delta?.status) patch.status = delta.status;
    else if (typeof r.status === 'string' && r.status) patch.status = r.status;
  }
  yield put(connectionSyncCompleted(connectionId, patch));

  if (delta) {
    // Project per-event mutations onto the calendar.events slice.
    // Use replaceEvent (full replace) for updated rows — proto3 omits
    // cleared optionals, so a merge-style update would leak stale
    // fields from the previous version.
    for (const ev of delta.created_events || []) {
      yield put(addEvent(ev as unknown as DomainCalendarEvent));
    }
    for (const ev of delta.updated_events || []) {
      yield put(replaceEvent(ev as unknown as DomainCalendarEvent));
    }
    for (const id of delta.deleted_event_ids || []) {
      yield put(removeEvent(id));
    }
  }
}

// ─── Apple credentials submit ─────────────────────────────

export function* appleSetupSaga(action: { type: string; payload: { email: string; appSpecificPassword: string } }): Generator {
  yield call(withSagaFlow, 'calendarConnections.apple_setup', ['calendar-connections'], action, function* () {
    yield put(setModalSubmitting(true));
    yield put(setModalError(null));
    try {
      const resp = (yield call(
        appleCalendarSetup,
        action.payload.email,
        action.payload.appSpecificPassword,
      )) as AppleCalendarSetupResponse;
      yield put(setPendingApple({
        email: action.payload.email,
        appSpecificPassword: action.payload.appSpecificPassword,
      }));
      yield put(setDiscoveredApple(resp.calendars || []));
      yield put(setModalProvider('apple'));
      yield put(setModalStep('subcalendar-picker'));
      yield* emitSagaPoint('api_appleCalendarSetup_ok', { count: resp.calendars?.length ?? 0 });
    } catch (error: any) {
      const message = errorMessage(error);
      yield put(setModalError(message));
      yield* emitSagaPoint('api_appleCalendarSetup_err', { message });
    } finally {
      yield put(setModalSubmitting(false));
    }
  });
}

// ─── Apple connect (after picker submit) ──────────────────

export function* appleConnectSaga(action: {
  type: string;
  payload: { email: string; appSpecificPassword: string; selectedCalendarUris: string[] };
}): Generator {
  yield call(withSagaFlow, 'calendarConnections.apple_connect', ['calendar-connections'], action, function* () {
    yield put(setModalSubmitting(true));
    yield put(setModalError(null));
    try {
      const resp = (yield call(appleCalendarConnect, action.payload)) as AppleCalendarConnectionResponse;
      yield* emitSagaPoint('api_appleCalendarConnection_ok', { id: resp.id });
      // Fire a sync so events show up immediately, then refresh the list.
      let syncResp: AppleCalendarSyncResponse | undefined;
      try {
        syncResp = (yield call(appleCalendarSync, resp.id)) as AppleCalendarSyncResponse;
      } catch (syncErr: any) {
        // Connection was created; surface sync issues but don't fail
        // the flow — user can hit "Sync now" from the row.
        yield* emitSagaPoint('api_appleCalendarManualSync_err_initial', { message: errorMessage(syncErr) });
      }
      yield put(closeConnectModal());
      yield put(fetchCalendarConnections());
      yield call(applySyncOutcomeSaga, resp.id, syncResp);
    } catch (error: any) {
      const message = errorMessage(error);
      yield put(setModalError(message));
      yield* emitSagaPoint('api_appleCalendarConnection_err', { message });
    } finally {
      yield put(setModalSubmitting(false));
    }
  });
}

// ─── Google: open OAuth popup ─────────────────────────────

export function* googleBeginSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'calendarConnections.google_begin', ['calendar-connections'], action, function* () {
    yield put(setModalSubmitting(true));
    yield put(setModalError(null));
    try {
      const redirectURL = `${window.location.origin}/calendar/google/callback`;
      const init = (yield call(googleCalendarAuthURL, redirectURL)) as GoogleAuthInitResponse;
      yield* emitSagaPoint('api_googleCalendarAuth_ok', {});
      // Open popup centered. The callback page postMessages back to
      // window.opener with code+state; the listener saga below picks
      // it up and dispatches GOOGLE_AFTER_CALLBACK.
      const popup = openCenteredPopup(init.auth_url, 'google-calendar-oauth', 540, 660);
      if (!popup) {
        yield put(setModalError('Popup blocked — allow popups for this site and try again.'));
        return;
      }
      // Listen for the callback message. Single-shot — the listener
      // tears itself down on either outcome. Also resolves (without
      // success) if the user closes the popup before completing OAuth,
      // so the modal's submitting state can clear and the user can
      // dismiss it.
      yield call(awaitGoogleCallback, init.state, popup);
    } catch (error: any) {
      const message = errorMessage(error);
      yield put(setModalError(message));
      yield* emitSagaPoint('api_googleCalendarAuth_err', { message });
    } finally {
      yield put(setModalSubmitting(false));
    }
  });
}

// ─── Google: after the popup posts back ───────────────────

export function* googleAfterCallbackSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'calendarConnections.google_after_callback', ['calendar-connections'], action, function* () {
    yield put(setModalSubmitting(true));
    yield put(setModalError(null));
    try {
      // Token has already been stored by the backend's callback
      // handler; we now just need to fetch the calendar list to
      // populate the picker.
      const resp = (yield call(listGoogleCalendars)) as GoogleCalendarListResponse;
      yield* emitSagaPoint('api_listGoogleCalendars_ok', { count: resp.items?.length ?? 0 });
      // Refresh connections so the new Google row is in state, then
      // park the modal on the picker.
      yield call(fetchConnectionsSaga, { type: FETCH_CONNECTIONS });
      const state = (yield select((s: any) => s.calendarConnections)) as { connections: CalendarConnection[] };
      const googleConn = state.connections.find((c) => c.provider === 'google');
      yield put(setDiscoveredGoogle(resp.items || []));
      yield put(setModalProvider('google'));
      yield put(setManaging(googleConn?.id ?? null));
      yield put(setModalStep('subcalendar-picker'));
    } catch (error: any) {
      const message = errorMessage(error);
      yield put(setModalError(message));
      yield* emitSagaPoint('api_listGoogleCalendars_err', { message });
    } finally {
      yield put(setModalSubmitting(false));
    }
  });
}

// ─── Save selection (post-connect picker submit, or "Manage") ─

export function* saveSelectionSaga(action: {
  type: string;
  payload: { connectionId: string; selectedIds: string[] };
}): Generator {
  yield call(withSagaFlow, 'calendarConnections.save_selection', ['calendar-connections'], action, function* () {
    yield put(setModalSubmitting(true));
    yield put(setModalError(null));
    try {
      yield call(updateConnectionSelection, action.payload.connectionId, action.payload.selectedIds);
      yield* emitSagaPoint('api_updateConnectionSelection_ok', {});
      // For Google, kick a sync so events for the newly-selected
      // calendars show up. Apple sync was already done at connect
      // time but we still re-sync here so a "Manage" edit takes
      // effect immediately.
      const state = (yield select((s: any) => s.calendarConnections)) as { connections: CalendarConnection[] };
      const conn = state.connections.find((c) => c.id === action.payload.connectionId);
      let syncResp: SyncOutcomeResponse;
      if (conn?.provider === 'google') {
        try { syncResp = (yield call(googleCalendarSync)) as GoogleCalendarSyncResponse | void; }
        catch { /* surface in next sync */ }
      } else if (conn?.provider === 'apple') {
        try { syncResp = (yield call(appleCalendarSync, conn.id)) as AppleCalendarSyncResponse; }
        catch { /* surface in next sync */ }
      }
      yield put(closeConnectModal());
      yield put(fetchCalendarConnections());
      yield call(applySyncOutcomeSaga, action.payload.connectionId, syncResp);
    } catch (error: any) {
      const message = errorMessage(error);
      yield put(setModalError(message));
      yield* emitSagaPoint('api_updateConnectionSelection_err', { message });
    } finally {
      yield put(setModalSubmitting(false));
    }
  });
}

// ─── Sync now ─────────────────────────────────────────────

export function* syncConnectionSaga(action: {
  type: string;
  payload: { connectionId: string; provider: string };
}): Generator {
  yield call(withSagaFlow, 'calendarConnections.sync', ['calendar-connections'], action, function* () {
    try {
      let syncResp: SyncOutcomeResponse;
      if (action.payload.provider === 'google') {
        syncResp = (yield call(googleCalendarSync)) as GoogleCalendarSyncResponse | void;
      } else {
        syncResp = (yield call(appleCalendarSync, action.payload.connectionId)) as AppleCalendarSyncResponse;
      }
      yield put(fetchCalendarConnections());
      yield call(applySyncOutcomeSaga, action.payload.connectionId, syncResp);
    } catch (error: any) {
      yield put(setConnectionsError(errorMessage(error)));
    }
  });
}

// ─── Disconnect ───────────────────────────────────────────

export function* disconnectSaga(action: {
  type: string;
  payload: { connectionId: string; provider: string };
}): Generator {
  yield call(withSagaFlow, 'calendarConnections.disconnect', ['calendar-connections'], action, function* () {
    try {
      // Snapshot the connection's subcalendar id list BEFORE the
      // disconnect RPC nukes the rows. We need these ids to project
      // the bulk event removal onto the local calendar.events slice —
      // the disconnect response doesn't carry per-event ids
      // (feedback_saga_full_delta: derive the delta when the server
      // can't return it). If the snapshot fails (e.g. transient
      // network), fall through with an empty list — the next
      // SyncPull will reconcile.
      let subcalendarIds: string[] = [];
      try {
        const rows = (yield call(listSubcalendars, action.payload.connectionId)) as Subcalendar[];
        subcalendarIds = (rows || []).map((r) => r.id);
      } catch (snapErr: any) {
        yield* emitSagaPoint('api_listSubcalendars_pre_disconnect_err', {
          message: errorMessage(snapErr),
        });
      }

      if (action.payload.provider === 'google') {
        yield call(googleCalendarDisconnect);
      }
      yield call(appleCalendarDisconnect, action.payload.connectionId);
      yield put(fetchCalendarConnections());
      yield put(
        calendarEventsRemovedByConnection(
          action.payload.connectionId,
          subcalendarIds,
        ),
      );
    } catch (error: any) {
      yield put(setConnectionsError(errorMessage(error)));
    }
  });
}

// ─── Open Manage on an existing row ───────────────────────
//
// Manage opens the rich per-subcalendar editor (toggle + color +
// member dropdown). It works for both providers because the
// subcalendar rows already carry display_name and don't need a
// re-discovery against Apple/Google.

export function* openManageSaga(action: { type: string; payload: string }): Generator {
  yield call(withSagaFlow, 'calendarConnections.open_manage', ['calendar-connections'], action, function* () {
    const state = (yield select((s: any) => s.calendarConnections)) as { connections: CalendarConnection[] };
    const conn = state.connections.find((c) => c.id === action.payload);
    if (!conn) return;
    yield put(setManaging(conn.id));
    yield put(setModalProvider(conn.provider as 'apple' | 'google'));
    yield put(setModalSubmitting(true));
    yield put(setModalError(null));
    try {
      const rows = (yield call(listSubcalendars, conn.id)) as Subcalendar[];
      yield put(setManageSubcalendars(rows));
      yield put(setModalStep('manage-subcalendars'));
    } catch (error: any) {
      const message = errorMessage(error);
      yield put(setModalError(message));
    } finally {
      yield put(setModalSubmitting(false));
    }
  });
}

// ─── Update a single subcalendar's enabled/color/owner ────

export function* updateSubcalendarSaga(action: {
  type: string;
  payload: { id: string; patch: { enabled?: boolean; color?: string; assignedOwnerId?: string } };
}): Generator {
  // Optimistic local patch so the UI feels responsive — server response
  // overwrites it on success; on error we re-fetch to recover state.
  const opt: Partial<Subcalendar> = {};
  if (action.payload.patch.enabled !== undefined) opt.enabled = action.payload.patch.enabled;
  if (action.payload.patch.color !== undefined) opt.color = action.payload.patch.color;
  if (action.payload.patch.assignedOwnerId !== undefined) opt.assigned_owner_id = action.payload.patch.assignedOwnerId;
  yield put(patchManageSubcalendar(action.payload.id, opt));
  try {
    const updated = (yield call(updateSubcalendarAPI, action.payload.id, action.payload.patch)) as Subcalendar;
    yield put(patchManageSubcalendar(updated.id, updated));
    // feedback_saga_full_delta: dispatch the post-write delta onto the
    // calendar.events slice instead of refetching. Color and owner
    // changes project cleanly onto existing rows (events carry
    // subcalendar_id + color + assigned_owner_id, denormalized at read
    // time). Enabled flips don't have an event-level counterpart in the
    // slice today — see the gap note below.
    const eventPatch: Partial<import('@/infra/types/domain').CalendarEvent> & {
      assigned_owner_id?: string;
    } = {};
    if (action.payload.patch.color !== undefined) {
      eventPatch.color = action.payload.patch.color;
    }
    if (action.payload.patch.assignedOwnerId !== undefined) {
      eventPatch.assigned_owner_id = action.payload.patch.assignedOwnerId;
    }
    if (Object.keys(eventPatch).length > 0) {
      yield put(calendarEventsPatched({ subcalendar_id: updated.id }, eventPatch));
    }
    yield* emitSagaPoint('api_updateSubcalendar_ok', {
      id: updated.id,
      fields: Object.keys(action.payload.patch),
    });
    // Gap: an `enabled` toggle is structural — flipping off should
    // remove the subcalendar's events from the local cache, flipping
    // on requires the server to materialize them. Until task 21
    // (CalendarConnectionDelta) carries per-event deltas in the
    // updateSubcalendar response, we still need a refetch for that
    // specific case. Color + owner edits stay refetch-free.
    if (action.payload.patch.enabled !== undefined) {
      yield put(fetchEvents());
    }
  } catch (error: any) {
    yield put(setModalError(errorMessage(error)));
    yield* emitSagaPoint('api_updateSubcalendar_err', { message: errorMessage(error) });
    // Recover by re-listing — the optimistic patch is now stale.
    const state = (yield select((s: any) => s.calendarConnections)) as { managingConnectionId: string | null };
    if (state.managingConnectionId) {
      try {
        const rows = (yield call(listSubcalendars, state.managingConnectionId)) as Subcalendar[];
        yield put(setManageSubcalendars(rows));
      } catch { /* leave stale, next refresh will fix */ }
    }
  }
}

// ─── Helpers ──────────────────────────────────────────────

function awaitGoogleCallback(expectedState: string, popup: Window): Promise<void> {
  return new Promise((resolve, reject) => {
    const handler = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      const data = ev.data || {};
      if (data.type === POPUP_SUCCESS && data.state === expectedState) {
        // We don't pass the code back to the main window — the popup
        // has already exchanged it via the backend callback handler.
        // We just need to know we can proceed.
        cleanup();
        // Schedule a microtask so the dispatch lands after this
        // event-handler returns.
        Promise.resolve().then(() => {
          import('@/infra/store').then((mod) => {
            mod.store.dispatch({ type: GOOGLE_AFTER_CALLBACK });
          });
        });
        resolve();
      } else if (data.type === POPUP_ERROR) {
        cleanup();
        reject(new Error(data.message || 'Google sign-in failed'));
      }
    };
    // Poll the popup so a manually-closed window doesn't leave the
    // parent modal stuck in submitting=true. Resolve (not reject) so
    // we don't surface a noisy error for a user-initiated cancel.
    const pollId = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
        resolve();
      }
    }, 500);
    const cleanup = () => {
      window.removeEventListener('message', handler);
      window.clearInterval(pollId);
    };
    window.addEventListener('message', handler);
  });
}

function openCenteredPopup(url: string, name: string, w: number, h: number): Window | null {
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  return window.open(
    url,
    name,
    `popup=yes,width=${w},height=${h},left=${left},top=${top}`,
  );
}

function errorMessage(err: any): string {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (err.response?.data?.error?.message) return err.response.data.error.message;
  if (err.response?.data?.message) return err.response.data.message;
  return err.message || 'Unknown error';
}

// ─── Root saga ────────────────────────────────────────────

export function* calendarConnectionsSaga(): Generator {
  yield takeLatest(FETCH_CONNECTIONS, fetchConnectionsSaga);
  yield takeLatest(APPLE_SETUP, appleSetupSaga);
  yield takeLatest(APPLE_CONNECT, appleConnectSaga);
  yield takeLatest(GOOGLE_BEGIN, googleBeginSaga);
  yield takeLatest(GOOGLE_AFTER_CALLBACK, googleAfterCallbackSaga);
  yield takeLatest(SAVE_SELECTION, saveSelectionSaga);
  yield takeLatest(SYNC_CONNECTION, syncConnectionSaga);
  yield takeLatest(DISCONNECT_CONNECTION, disconnectSaga);
  yield takeLatest(OPEN_MANAGE, openManageSaga);
  yield takeLatest(UPDATE_SUBCAL, updateSubcalendarSaga);
}
